import { isNativo, piattaformaNativa } from "~/utils/nativo.js";
import { azioneTastoIndietro } from "~/utils/tastoIndietro.js";
import { biometriaAttiva, creaControlloRipresa, obbligoBiometria, sbloccaConBiometria, serveBiometria } from "~/utils/biometria.js";
import { cancellaToken, tokenDaCookieDocumento } from "~/utils/tokenLocale.js";

// Adattamenti dell'app nativa (shell Capacitor): barra di stato, tastiera, splash, tasto
// indietro e stato della rete. Sul web esce subito e non cambia nulla.
export default defineNuxtPlugin((nuxtApp) => {
  if (!isNativo()) return;

  const router = useRouter();
  const online = useState("online", () => true);
  const nomeCookieToken = `token${useRuntimeConfig().public.base_progetto}`;

  (async () => {
    try {
      const { StatusBar } = await import("@capacitor/status-bar");
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch (e) {
      console.warn("[nativo] status bar", e);
    }

    // setResizeMode esiste solo su iOS.
    if (piattaformaNativa() === "ios") {
      try {
        const { Keyboard } = await import("@capacitor/keyboard");
        await Keyboard.setResizeMode({ mode: "body" });
        // In modalità "body" vh e dvh non cambiano con la tastiera aperta: chi deve finire
        // sopra la tastiera (LoginShell, classe segue-tastiera) usa --tastiera.
        const radice = document.documentElement.style;
        await Keyboard.addListener("keyboardWillShow", ({ keyboardHeight }) => {
          radice.setProperty("--tastiera", `${keyboardHeight}px`);
        });
        await Keyboard.addListener("keyboardWillHide", () => {
          radice.setProperty("--tastiera", "0px");
        });
        // Il campo toccato resta visibile nel contenitore accorciato.
        await Keyboard.addListener("keyboardDidShow", () => {
          const campo = document.activeElement;
          if (campo?.closest?.(".segue-tastiera")) campo.scrollIntoView({ block: "center" });
        });
      } catch (e) {
        console.warn("[nativo] tastiera", e);
      }
    }

    try {
      const { App } = await import("@capacitor/app");
      await App.addListener("backButton", () => {
        const azione = azioneTastoIndietro({
          percorso: router.currentRoute.value.path,
          dialogAperti: document.querySelector(".p-dialog-mask") !== null,
        });
        if (azione === "chiudi-dialog") {
          // PrimeVue chiude il dialog con Escape.
          document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", keyCode: 27, bubbles: true }));
        } else if (azione === "indietro") {
          // Senza cronologia router.back() non fa nulla: si esce.
          if (window.history.length <= 1) App.exitApp();
          else router.back();
        } else {
          App.exitApp();
        }
      });
    } catch (e) {
      console.warn("[nativo] tasto indietro", e);
    }

    // Rientro dal background dopo qualche minuto: Face ID / impronta, se attivati o obbligatori.
    // Al rifiuto il token locale si cancella e l'app ricarica il login (stato pulito).
    // All'avvio da chiusa lo sblocco lo chiede index.vue.
    try {
      const { App } = await import("@capacitor/app");
      const controlla = creaControlloRipresa({
        serve: async () => {
          const percorso = router.currentRoute.value.path;
          if (percorso === "/" || percorso.startsWith("/verifica_dispositivo")) return false;
          return serveBiometria({
            nativo: true,
            attiva: await biometriaAttiva(),
            obbligatoria: await obbligoBiometria(),
            tokenPresente: !!tokenDaCookieDocumento(document.cookie, nomeCookieToken),
          });
        },
        sblocca: sbloccaConBiometria,
        esci: async () => {
          await nuxtApp.runWithContext(() => cancellaToken());
          window.location.replace("/");
        },
      });
      await App.addListener("appStateChange", (stato) => controlla(stato).catch((e) => console.warn("[nativo] sblocco al rientro", e)));
    } catch (e) {
      console.warn("[nativo] sblocco al rientro", e);
    }

    try {
      const { Network } = await import("@capacitor/network");
      online.value = (await Network.getStatus()).connected;
      await Network.addListener("networkStatusChange", (stato) => {
        online.value = stato.connected;
      });
    } catch (e) {
      console.warn("[nativo] rete", e);
    }
  })();

  // La splash si nasconde quando l'app è montata (una volta sola).
  nuxtApp.hook("app:mounted", async () => {
    try {
      const { SplashScreen } = await import("@capacitor/splash-screen");
      await SplashScreen.hide();
    } catch (e) {
      console.warn("[nativo] splash", e);
    }
  });
});
